import { AlertTriangle, Check, Loader2, Lock, Shield } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { toastApiError } from "@/shared/utils/toast";

import type { AccessArea } from "../../constants/accessAreas.constants";
import type { AccessRole } from "../../types/access.types";
import { gatherStatements, resolveCell } from "../../utils/denyFirst";
import {
  areaLevelTargets,
  cellKey,
  indexByCell,
  roleCell,
  type AreaLevel,
  type PermissionCell,
} from "../../utils/permissionCells";
import { roleColor } from "../../utils/roleColor";
import { AccessSavedIndicator, type SaveStatus } from "./AccessSavedIndicator";
import { EffectiveMatrix } from "./EffectiveMatrix";
import { PermissionBoard, type BoardCellState } from "./PermissionBoard";
import { PrincipalAvatar } from "./PrincipalAvatar";

/** One role switched on or off, as part of a save. */
interface RoleChange {
  roleId: string;
  on: boolean;
}

export interface AssignRolesPrincipal {
  id: string;
  name: string;
  kind: "person" | "key";
  /** Email (person) or masked prefix (key). */
  subtitle?: string;
}

export interface AssignRolesDrawerProps {
  principal: AssignRolesPrincipal | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Full roles catalog (with policies) — drives grouping and the live preview. */
  roles: readonly AccessRole[];
  /** Role ids currently assigned to the principal. */
  assignedRoleIds: readonly string[];
  /** When false the drawer is read-only (view, not edit). */
  canManage: boolean;
  /** Persist an added role (parent calls the API and updates its state). */
  onAssign: (roleId: string) => Promise<unknown>;
  /** Persist a removed role. */
  onRemove: (roleId: string) => Promise<unknown>;
}

/**
 * Shared right-side drawer to assign roles to a principal (person now, API key later). Auto-saves: each
 * toggle persists immediately and surfaces the "changes saved automatically" cue. The live effective matrix
 * and the deny notice recompute from the current selection. Read-only when `canManage` is false.
 */
export function AssignRolesDrawer({
  principal,
  open,
  onOpenChange,
  roles,
  assignedRoleIds,
  canManage,
  onAssign,
  onRemove,
}: AssignRolesDrawerProps) {
  const { t } = useTranslations("settings");
  const [selected, setSelected] = useState<Set<string>>(() => new Set(assignedRoleIds));
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [seededId, setSeededId] = useState<string | null>(null);
  const [roleQuery, setRoleQuery] = useState("");
  /**
   * What is being saved — a role id, a board cell (`cellKey`) or an area (`area:<id>`) — so that control can
   * show the wait instead of a global lock.
   */
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-seed the local selection on a different principal AND on every open. Keying on `open` (closed → null)
  // mirrors the editor drawers: closing resets `seededId`, so reopening the SAME principal re-reads the
  // authoritative `assignedRoleIds` instead of stale local state (e.g. roles changed in the background, or a
  // previous save left `status="error"`). While open for the same principal `currentId === seededId`, so
  // local state stays authoritative and optimistic toggles aren't clobbered by parent re-renders.
  const currentId = open ? (principal?.id ?? null) : null;
  if (currentId !== seededId) {
    setSeededId(currentId);
    if (open) {
      setSelected(new Set(assignedRoleIds));
      setStatus("idle");
      // The filter describes THIS principal: carried over, the drawer would open already filtered by a term
      // typed for someone else. (The board's expanded areas reset through its `key`.)
      setRoleQuery("");
    }
  }

  // Which principal the drawer is currently showing, readable from an async continuation. Written in an
  // effect rather than during render so the ref lint rule stays happy.
  const sessionRef = useRef<string | null>(null);
  useEffect(() => {
    sessionRef.current = currentId;
  }, [currentId]);

  useEffect(() => {
    if (status !== "saved") return;
    savedTimer.current = setTimeout(() => setStatus("idle"), 2500);
    return () => {
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, [status]);

  /**
   * Auto-save. One save at a time, and the lock is VISIBLE rather than silent: a click that cannot land must
   * not look like it did, or the operator believes they assigned five roles when one landed. Several changes
   * (an area shortcut) go one request after another; on a failure the selection keeps what actually landed.
   */
  const runChanges = async (changes: readonly RoleChange[], key: string) => {
    if (!canManage || pendingKey !== null || changes.length === 0) return;
    // Whose save this is. The drawer keeps its state while closed — it renders `null`, it does not unmount —
    // so a save can still be in the air when it reopens on someone else, and the failure path below writes
    // a whole role set back into the form.
    const owner = currentId;
    const optimistic = new Set(selected);
    for (const change of changes) {
      if (change.on) optimistic.add(change.roleId);
      else optimistic.delete(change.roleId);
    }
    const landed = new Set(selected);
    setSelected(optimistic);
    setPendingKey(key);
    setStatus("saving");
    try {
      for (const change of changes) {
        if (change.on) await onAssign(change.roleId);
        else await onRemove(change.roleId);
        if (change.on) landed.add(change.roleId);
        else landed.delete(change.roleId);
      }
      if (sessionRef.current === owner) setStatus("saved");
    } catch (error) {
      if (sessionRef.current === owner) {
        setSelected(landed); // keep what the server accepted, drop the rest
        setStatus("error");
      }
      // The failure is real whoever is on screen now — the operator has to hear about it either way.
      toastApiError(error);
    } finally {
      // Unconditional: it belongs to the save that just finished, and it is what unblocks the drawer. While
      // it was non-null no other save could start, so it cannot clobber anyone else's.
      setPendingKey(null);
    }
  };

  const toggleRole = (roleId: string) => runChanges([{ roleId, on: !selected.has(roleId) }], roleId);

  const selectedRoles = useMemo(
    () => [...selected].map((id) => roles.find((r) => r.id === id)).filter((r): r is AccessRole => !!r),
    [selected, roles],
  );
  const statements = useMemo(() => gatherStatements([...selected], roles), [selected, roles]);
  const denyPolicies = useMemo(() => {
    const names = new Set<string>();
    for (const role of selectedRoles) {
      for (const policy of role.policies) {
        if (policy.effect === "deny") names.add(policy.name);
      }
    }
    return [...names];
  }, [selectedRoles]);

  // Base roles grant one permission each (`cards_read`) and become toggles on the board; roles that combine
  // several, deny, or belong to the organization keep a row of their own above it.
  const roleIndex = useMemo(() => indexByCell(roles, roleCell), [roles]);

  // Search over what an operator can actually read on the row, plus the slug — `cards_read` is what a
  // developer looking at an API response would paste in. The board filters its own modules by the same term.
  const term = roleQuery.trim().toLowerCase();
  const combinedRoles = useMemo(
    () =>
      roleIndex.rest.filter(
        (role) =>
          !term ||
          role.name.toLowerCase().includes(term) ||
          role.slug.toLowerCase().includes(term) ||
          (role.description?.toLowerCase().includes(term) ?? false),
      ),
    [roleIndex, term],
  );

  const backingRoles = (cell: PermissionCell) =>
    roleIndex.byCell.get(cellKey(cell.moduleId, cell.atom)) ?? [];

  const cellState = (cell: PermissionCell): BoardCellState => {
    const backing = backingRoles(cell);
    if (backing.some((role) => selected.has(role.id))) return "on";
    const effective = resolveCell(statements, cell.moduleId, cell.atom).effect;
    if (effective === "allow") return "covered";
    if (effective === "deny") return "denied";
    return backing.length > 0 ? "off" : "unavailable";
  };

  /** The saves that turn one cell on (its canonical role) or off (every held role granting it). */
  const cellChanges = (cell: PermissionCell, on: boolean): RoleChange[] => {
    const backing = backingRoles(cell);
    if (on) return backing.some((role) => selected.has(role.id)) ? [] : [{ roleId: backing[0].id, on: true }];
    return backing.filter((role) => selected.has(role.id)).map((role) => ({ roleId: role.id, on: false }));
  };

  const toggleCell = (cell: PermissionCell) => {
    const state = cellState(cell);
    if (state !== "on" && state !== "off") return;
    void runChanges(cellChanges(cell, state === "off"), cellKey(cell.moduleId, cell.atom));
  };

  const applyLevel = (area: AccessArea, level: AreaLevel) => {
    const changes: RoleChange[] = [];
    for (const target of areaLevelTargets(area, level)) {
      const state = cellState(target);
      if (state === "off" && target.on) changes.push(...cellChanges(target, true));
      if (state === "on" && !target.on) changes.push(...cellChanges(target, false));
    }
    void runChanges(changes, `area:${area.id}`);
  };

  if (!principal) return null;

  const title = (
    <span className="flex items-center gap-2.5">
      <PrincipalAvatar name={principal.name} kind={principal.kind} size={30} />
      <span className="flex items-center gap-2">
        {principal.name}
        {principal.kind === "key" && (
          <ColocaBadge variant="secondary">{t("access_badge_api_key")}</ColocaBadge>
        )}
      </span>
    </span>
  );

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={title}
      description={principal.subtitle}
      footer={
        <div className="flex w-full items-center gap-2">
          <span className="text-[11.5px] text-muted-foreground">
            {canManage ? t("access_drawer_apply_immediately") : t("access_drawer_readonly")}
          </span>
          <AccessSavedIndicator status={status} className="ml-1" />
          <ColocaButton variant="default" size="sm" className="ml-auto" onClick={() => onOpenChange(false)}>
            {t("access_drawer_close")}
          </ColocaButton>
        </div>
      }
    >
      <div className="mb-2.5 font-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-foreground">
        {t("access_drawer_roles_count", { count: selected.size })}
      </div>

      <div className="mb-3">
        <ColocaSearchInput
          value={roleQuery}
          onValueChange={setRoleQuery}
          placeholder={t("access_drawer_search_roles_placeholder")}
        />
      </div>

      {combinedRoles.length > 0 && (
        <section className="mb-4" aria-labelledby="assign-roles-combined">
          <h4 id="assign-roles-combined" className="mb-0.5 text-[12.5px] font-semibold text-foreground">
            {t("access_drawer_combined_roles")}
          </h4>
          <p className="mb-2 text-[11.5px] text-muted-foreground">{t("access_drawer_combined_hint")}</p>
          <RoleAssignRows
            roles={combinedRoles}
            selected={selected}
            readOnly={!canManage}
            pendingKey={pendingKey}
            onToggle={toggleRole}
          />
        </section>
      )}

      <h4 className="mb-0.5 text-[12.5px] font-semibold text-foreground">
        {t("access_drawer_permissions_label")}
      </h4>
      <p className="mb-2 text-[11.5px] text-muted-foreground">{t("access_drawer_permissions_hint")}</p>
      <PermissionBoard
        key={principal.id}
        query={roleQuery}
        cellState={cellState}
        onToggle={canManage ? toggleCell : undefined}
        onApplyLevel={canManage ? applyLevel : undefined}
        pendingKey={pendingKey}
        busy={pendingKey !== null}
      />

      {denyPolicies.length > 0 && (
        <div className="mt-4 flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-[12px] text-foreground">
          <AlertTriangle size={13} className="mt-0.5 shrink-0 text-destructive" />
          <span>{t("access_drawer_deny_notice", { count: denyPolicies.length })}</span>
        </div>
      )}

      <div className="mt-4 shrink-0 overflow-hidden rounded-md border border-border">
        <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-3.5 py-2.5">
          <Shield size={13} className="text-primary" />
          <span className="text-[12.5px] font-semibold text-foreground">
            {t("access_drawer_effective_title")}
          </span>
          <span className="ml-auto text-[11px] text-muted-foreground">
            {t("access_drawer_effective_hint")}
          </span>
        </div>
        <div className="p-2.5">
          {selected.size === 0 ? (
            <div className="px-2 py-4 text-center text-[12.5px] text-muted-foreground">
              {t("access_drawer_no_roles_empty")}
            </div>
          ) : (
            <EffectiveMatrix statements={statements} />
          )}
        </div>
      </div>
    </ColocaDrawer>
  );
}

interface RoleAssignRowsProps {
  roles: readonly AccessRole[];
  selected: Set<string>;
  readOnly: boolean;
  /** What is being saved; every row is inert while anything is, but only the matching row shows it. */
  pendingKey: string | null;
  onToggle: (roleId: string) => void;
}

function RoleAssignRows({ roles, selected, readOnly, pendingKey, onToggle }: RoleAssignRowsProps) {
  const { t } = useTranslations("settings");

  return (
    <div>
      {roles.length === 0 ? (
        <div className="px-0.5 py-1 text-[11.5px] text-muted-foreground/70">
          {t("access_drawer_no_roles_available")}
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {roles.map((role) => {
            const on = selected.has(role.id);
            const isGlobal = !role.organization_id;
            const hasDeny = role.policies.some((p) => p.effect === "deny");
            const isPending = pendingKey === role.id;
            // Visibly inert while ANY save is in flight, rather than accepting the click and dropping it.
            const isBusy = pendingKey !== null;
            return (
              <button
                key={role.id}
                type="button"
                role="checkbox"
                aria-checked={on}
                disabled={readOnly || isBusy}
                onClick={() => onToggle(role.id)}
                className={cn(
                  "flex items-center gap-2.5 rounded-md border px-2.5 py-2 text-left transition-colors",
                  on ? "border-primary bg-primary/10" : "border-border bg-card hover:border-border/80",
                  readOnly && "cursor-default",
                  isBusy && !isPending && "opacity-50",
                )}
              >
                <span
                  className={cn(
                    "grid h-[18px] w-[18px] shrink-0 place-items-center rounded border",
                    on ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card",
                  )}
                >
                  {isPending ? <Loader2 size={11} className="animate-spin" /> : on && <Check size={11} />}
                </span>
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: roleColor(role.slug) }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="text-[12.5px] font-semibold text-foreground">{role.name}</span>
                    {isGlobal && <Lock size={9} className="text-muted-foreground/70" />}
                    {hasDeny && (
                      <span className="font-mono text-[9px] font-bold text-destructive">
                        {t("access_policy_effect_deny")}
                      </span>
                    )}
                  </span>
                  {role.description && (
                    <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                      {role.description}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
